/**
 * ExpressionInput.js — Input for static numbers or dynamic character references.
 *
 * The engine supports both static numbers and { "$ref": "character.traits.Knowledge" }
 * expressions. This input shows radio buttons to pick the mode, then the
 * appropriate input (number field or stat dropdown).
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import { useState } from 'react';
import { cn } from '../../../../lib/utils';

const STAT_OPTIONS = [
  { label: 'Agility', value: 'character.traits.Agility' },
  { label: 'Strength', value: 'character.traits.Strength' },
  { label: 'Finesse', value: 'character.traits.Finesse' },
  { label: 'Instinct', value: 'character.traits.Instinct' },
  { label: 'Presence', value: 'character.traits.Presence' },
  { label: 'Knowledge', value: 'character.traits.Knowledge' },
  { label: 'Level', value: 'character.level' },
  { label: 'Max HP', value: 'character.maxHp' },
  { label: 'Proficiency', value: 'character.proficiency' },
];

export default function ExpressionInput({ value, onChange }) {
  const isExpression = value && typeof value === 'object' && value.$ref !== undefined;
  const [mode, setMode] = useState(isExpression ? 'trait' : 'fixed');

  const switchMode = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    onChange(newMode === 'fixed' ? 1 : { $ref: '' });
  };

  return (
    <div className="space-y-1.5">
      {/* Radio buttons */}
      <div className="flex rounded-md overflow-hidden border border-gray-600">
        <button
          type="button"
          onClick={() => switchMode('fixed')}
          className={cn(
            'flex-1 px-3 py-1 text-xs font-medium transition-colors',
            mode === 'fixed'
              ? 'bg-red-900/50 text-red-300 border-r border-gray-600'
              : 'bg-gray-800 text-gray-400 hover:text-gray-300 border-r border-gray-600',
          )}
        >
          Fixed Number
        </button>
        <button
          type="button"
          onClick={() => switchMode('trait')}
          className={cn(
            'flex-1 px-3 py-1 text-xs font-medium transition-colors',
            mode === 'trait'
              ? 'bg-red-900/50 text-red-300'
              : 'bg-gray-800 text-gray-400 hover:text-gray-300',
          )}
        >
          Character Trait
        </button>
      </div>

      {/* Input */}
      {mode === 'fixed' ? (
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={e => onChange(parseInt(e.target.value) || 0)}
          placeholder="e.g. 3"
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
        />
      ) : (
        <select
          value={isExpression ? value.$ref : ''}
          onChange={e => onChange({ $ref: e.target.value })}
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
        >
          <option value="">Select a stat...</option>
          {STAT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );
}
